/**
 * Settings landing page (#2425).
 *
 * Replaces a scrolling row of sixteen tabs. Every setting is visible at once,
 * bucketed by what it is: the workshop's own details, the work it does, the
 * space it does it in, and the system around it. A group whose every entry is
 * behind a permission the user lacks is dropped rather than shown empty.
 *
 * The card's header is itself clickable (#2699) and opens the group at its
 * first entry — from there the group's other entries are tabs on the page, so
 * the user browses a card's contents without returning here between topics.
 * Only the header: the entries below are already buttons, and a button inside
 * a button is invalid markup that breaks keyboard navigation.
 *
 * Wide screens show two independent columns (#3664). Cards are dealt left,
 * right, left, right, and each column stacks on its own, so a short card does
 * not leave a hole above the card under it. A narrow screen stays one column
 * in canonical group order.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SETTINGS_GROUP_LABEL_KEY, SETTINGS_TAB_META } from './settingsTabMeta';
import { getSettingsGroups, type SettingsGroup, type SettingsTab } from './settingsTabs';
import { packSettingsGroups } from './settingsHomeColumns';

interface Props {
  allowedTabs: SettingsTab[];
  onOpen: (tab: SettingsTab) => void;
}

interface GroupEntry {
  group: SettingsGroup;
  tabs: SettingsTab[];
}

function SettingsGroupCard({
  group,
  tabs,
  onOpen,
}: GroupEntry & { onOpen: (tab: SettingsTab) => void }) {
  const { t } = useTranslation('settings');
  // getSettingsGroups never yields an empty group, so there is always a
  // first entry to land on.
  const firstTab = tabs[0];

  return (
    <Card variant="outlined">
      <CardActionArea
        onClick={() => { if (firstTab) onOpen(firstTab); }}
        data-testid={`settings-home-group-${group}`}
        aria-label={t(SETTINGS_GROUP_LABEL_KEY[group])}
      >
        <CardContent sx={{ pb: 0 }}>
          <Typography variant="h6" fontWeight={600}>
            {t(SETTINGS_GROUP_LABEL_KEY[group])}
          </Typography>
        </CardContent>
      </CardActionArea>
      <List disablePadding sx={{ py: 1 }}>
        {tabs.map((tab) => {
          const meta = SETTINGS_TAB_META[tab];
          return (
            <ListItemButton
              key={tab}
              onClick={() => { onOpen(tab); }}
              data-testid={`settings-home-${tab}`}
            >
              <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                {meta.icon}
              </ListItemIcon>
              <ListItemText
                primary={t(meta.labelKey)}
                secondary={t(meta.descriptionKey)}
                slotProps={{
                  primary: { fontWeight: 500 },
                  secondary: { variant: 'body2' },
                }}
              />
              <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );
}

export function SettingsHome({ allowedTabs, onOpen }: Props) {
  const theme = useTheme();
  // noSsr: the portal is a client app, and the default double render would
  // flash one column before the wide layout.
  const wide = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const groups = getSettingsGroups(allowedTabs);
  const columns = packSettingsGroups(groups, wide ? 2 : 1);

  return (
    <Box
      data-testid="settings-home"
      sx={{
        display: 'flex',
        flexDirection: wide ? 'row' : 'column',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      {columns.map((column) => (
        <Box
          key={column.map((entry) => entry.group).join('-')}
          {...(wide ? { 'data-testid': 'settings-home-column' } : {})}
          sx={{
            flex: '1 1 0',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            ...(wide ? {} : { width: '100%' }),
          }}
        >
          {column.map((entry) => (
            <SettingsGroupCard
              key={entry.group}
              group={entry.group}
              tabs={entry.tabs}
              onOpen={onOpen}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
